import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Star, Phone, MessageCircle, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../AdminLayout";
import { adminAxios, formatErr } from "../api";
import { useCatalog } from "../../contexts/CatalogContext";
import { StatusPill } from "./AdminDashboard";

export default function AdminTechnicianDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tech, setTech] = useState(null);
  const { categories: CATEGORIES } = useCatalog();

  const load = () => {
    adminAxios.get(`/technicians/${id}`).then((r) => setTech(r.data)).catch((e) => toast.error(formatErr(e)));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const toggleStatus = async () => {
    const next = tech.status === "active" ? "deactivated" : "active";
    try {
      const { data } = await adminAxios.patch(`/technicians/${id}`, { status: next });
      setTech((t) => ({ ...t, ...data }));
      toast.success(`Technician ${next === "active" ? "activated" : "deactivated"}`);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  if (!tech) return <AdminLayout title="Technician"><div className="text-slate-500">Loading…</div></AdminLayout>;

  const isActive = tech.status === "active";

  return (
    <AdminLayout title={tech.name}>
      <button onClick={() => navigate(-1)} className="mb-4 text-sm font-semibold text-slate-500 hover:text-[#1A3C6E] flex items-center gap-1" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-start gap-5">
            <div className="w-16 h-16 rounded-full bg-[#1A3C6E] text-white flex items-center justify-center font-bold text-2xl">
              {tech.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="font-display font-bold text-xl text-slate-900">{tech.name}</h2>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isActive ? "bg-[#DCFCE7] text-[#22C55E]" : "bg-slate-100 text-slate-600"}`}>
                  {isActive ? "Active" : "Deactivated"}
                </span>
              </div>
              <div className="text-sm text-slate-500 mt-1 capitalize">{tech.city} • {tech.experience}</div>
              <div className="mt-3 flex gap-3 text-sm">
                <a href={`tel:${tech.phone}`} className="inline-flex items-center gap-1.5 text-[#1A3C6E] font-semibold" data-testid="tech-call">
                  <Phone className="w-4 h-4" /> {tech.phone}
                </a>
                <a href={`https://wa.me/91${tech.phone}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#22C55E] font-semibold" data-testid="tech-whatsapp">
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </div>
            </div>
          </div>

          <Section title="Skills">
            <div className="flex flex-wrap gap-2 mb-3">
              {(tech.services || []).map((sid) => {
                const c = CATEGORIES.find((c) => c.id === sid);
                return (
                  <span key={sid} className="px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: c?.bg || "#E2E8F0", color: c?.color || "#475569" }}>
                    {c?.short || sid}
                  </span>
                );
              })}
              {(!tech.services || tech.services.length === 0) && <span className="text-sm text-slate-400">No skills listed.</span>}
            </div>
            <div className="text-xs text-slate-500 mt-2">Brands</div>
            <div className="text-sm text-slate-700">{(tech.brands || []).join(", ") || "—"}</div>
          </Section>

          <Section title="Performance">
            <div className="grid grid-cols-3 gap-4">
              <Metric label="Jobs done" value={tech.jobs_completed || 0} />
              <Metric label="Rating" value={(tech.rating || 0).toFixed(1)} icon={Star} />
              <Metric label="Recent bookings" value={tech.bookings?.length || 0} />
            </div>
            <div className="mt-5">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Recent bookings</div>
              {(tech.bookings || []).length === 0 ? (
                <div className="text-sm text-slate-400">No bookings assigned yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 uppercase">
                    <tr><th className="text-left py-2">ID</th><th className="text-left py-2">Customer</th><th className="text-left py-2">Date</th><th className="text-left py-2">Status</th></tr>
                  </thead>
                  <tbody>
                    {tech.bookings.slice(0, 8).map((b) => (
                      <tr key={b.booking_id} className="border-t border-slate-100">
                        <td className="py-2 font-mono text-[#1A3C6E]">{b.booking_id}</td>
                        <td className="py-2">{b.name}</td>
                        <td className="py-2">{b.date}</td>
                        <td className="py-2"><StatusPill status={b.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Section>

          <Section title="Bank & UPI">
            <Field k="Account" v={tech.bank_account || "—"} />
            <Field k="IFSC" v={tech.ifsc || "—"} />
            <Field k="UPI ID" v={tech.upi_id || "—"} />
          </Section>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-500 mb-3">Actions</h3>
            <button
              onClick={toggleStatus}
              data-testid="toggle-status"
              className={`w-full inline-flex items-center justify-center gap-2 font-semibold py-2.5 rounded-lg ${
                isActive ? "bg-slate-100 hover:bg-slate-200 text-slate-700" : "bg-[#22C55E] hover:bg-[#16A34A] text-white"
              }`}
            >
              {isActive ? <ToggleLeft className="w-4 h-4" /> : <ToggleRight className="w-4 h-4" />}
              {isActive ? "Deactivate" : "Activate"}
            </button>
            <p className="text-xs text-slate-400 mt-3">Deactivated technicians can't be assigned new jobs but their booking history stays intact.</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-500 mb-3">{title}</h3>
    {children}
  </div>
);

const Field = ({ k, v }) => (
  <div className="flex justify-between gap-3 py-1.5">
    <span className="text-sm text-slate-500">{k}</span>
    <span className="text-sm font-semibold text-slate-900">{v}</span>
  </div>
);

const Metric = ({ label, value, icon: Icon }) => (
  <div className="bg-slate-50 rounded-lg p-3 text-center">
    <div className="text-xs text-slate-500">{label}</div>
    <div className="mt-1 font-display font-bold text-xl text-slate-900 flex items-center justify-center gap-1">
      {Icon && <Icon className="w-4 h-4 text-[#FF6B2B] fill-[#FF6B2B]" />}
      {value}
    </div>
  </div>
);
